import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError, setToken, getToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
    const [perms, setPerms] = useState({}); // {key: bool}
    const [error, setError] = useState("");

    const loadPerms = useCallback(async () => {
        try {
            const { data } = await api.get("/me/permissions");
            setPerms(data || {});
        } catch { setPerms({}); }
    }, []);

    const refresh = useCallback(async () => {
        if (!getToken()) { setUser(false); setPerms({}); return; }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            await loadPerms();
        } catch {
            setToken(null);
            setUser(false);
            setPerms({});
        }
    }, [loadPerms]);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async ({ email, codice, password }) => {
        setError("");
        try {
            const payload = { password };
            if (email) payload.email = email;
            if (codice) payload.codice = codice;
            const { data } = await api.post("/auth/login", payload);
            if (data.access_token) setToken(data.access_token);
            setUser(data);
            await loadPerms();
            return data;  // ritorna l'utente per permettere redirect role-aware
        } catch (e) {
            setError(formatApiError(e));
            return null;
        }
    };

    const register = async (name, email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", { name, email, password });
            if (data.access_token) setToken(data.access_token);
            setUser(data);
            await loadPerms();
            return true;
        } catch (e) {
            setError(formatApiError(e));
            return false;
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { console.error("logout call failed", e); }
        setToken(null);
        setUser(false);
        setPerms({});
    };

    const can = useCallback((key) => {
        if (!user) return false;
        if (user.role === "admin") return true;
        return !!perms[key];
    }, [user, perms]);

    // Memoizza il value per evitare re-render inutili dei consumer del context
    const ctxValue = useMemo(
        () => ({ user, perms, can, error, setError, login, register, logout, refresh, refreshPerms: loadPerms }),
        [user, perms, can, error, refresh, loadPerms]
    );

    return (
        <AuthCtx.Provider value={ctxValue}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
